import { FileText } from 'lucide-react';

const LAST_UPDATED = '11 July 2026';

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto">
      <section className="text-center py-12">
        <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
          <FileText className="w-4 h-4" />
          Legal
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
        <p className="text-gray-500">Last updated: {LAST_UPDATED}</p>
      </section>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 md:p-12 space-y-8 mb-16 prose prose-sm max-w-none">

        <section>
          <p className="text-gray-600 leading-relaxed">
            These Terms & Conditions ("Terms") form a legal agreement between you ("you", "user", or "end user") and{' '}
            <strong>VarBiz Ltd</strong> ("we", "us", "our", "RemindMeOf"), the operator of the RemindMeOf service
            available at remindmeof.co.uk (the "Service"). By creating an account or otherwise using the Service, you
            confirm that you have read, understood, and agree to be bound by these Terms and our{' '}
            <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>. If you do not agree, you
            must not use the Service.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">1. Description of the Service</h2>
          <p className="text-gray-600 leading-relaxed">
            RemindMeOf lets you create reminders and receive notifications about them (for example, by email) ahead of
            dates you specify. The Service is provided free of charge on a best-effort basis and is not a substitute
            for your own record-keeping, calendars, or professional advice.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">2. Eligibility</h2>
          <p className="text-gray-600 leading-relaxed">
            You must be at least 13 years old to use the Service. By registering, you confirm that the information you
            provide is accurate and that you meet this age requirement.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">3. Your Account</h2>
          <p className="text-gray-600 leading-relaxed">
            You are responsible for maintaining the confidentiality of your login credentials and for all activity
            under your account. You must provide accurate reminder details and keep your contact information (such as
            your email address) up to date, since this is how notifications are delivered to you.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">4. Acceptable Use</h2>
          <p className="text-gray-600 leading-relaxed mb-3">You agree not to:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Use the Service for any unlawful, fraudulent, or abusive purpose.</li>
            <li>Attempt to disrupt, reverse-engineer, or gain unauthorised access to the Service or its infrastructure.</li>
            <li>Use the Service to store or transmit content that infringes the rights of others or violates applicable law.</li>
            <li>Create multiple accounts to circumvent any limits placed on the Service.</li>
          </ul>
        </section>

        <section className="border-2 border-amber-200 bg-amber-50 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">5. No Guarantee of Reminders — Important Disclaimer</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            <strong>The Service is a convenience tool only.</strong> Reminders and notifications are sent on a
            best-effort basis and may not always be delivered, or may be delayed, filtered as spam, blocked, or
            otherwise fail to reach you. This can happen for reasons including but not limited to: email or network
            outages, third-party provider failures (including our email delivery and hosting providers), incorrect or
            outdated contact details, spam or junk filtering by your email provider, incorrect information entered by
            you when creating a reminder, or unforeseen technical faults.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3">
            <strong>You remain solely and entirely responsible</strong> for tracking, actioning, and meeting your own
            deadlines, appointments, payments, obligations, and events. You must not rely exclusively on RemindMeOf
            for anything time-critical or important, and should maintain your own independent record of any date or
            obligation that matters to you.
          </p>
          <p className="text-gray-700 leading-relaxed">
            To the fullest extent permitted by law, <strong>VarBiz Ltd, its owners, directors, and staff accept no
            liability whatsoever</strong> for any loss, damage, cost, expense, missed deadline, missed payment, missed
            appointment, financial loss, or other direct, indirect, or consequential harm arising from the Service
            failing to send, or you failing to receive or act on, any reminder or notification. By using the Service,
            you accept this risk and agree that any consequence of a reminder not being delivered or actioned is your
            responsibility alone, not RemindMeOf's.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">6. Disclaimer of Warranties</h2>
          <p className="text-gray-600 leading-relaxed">
            The Service is provided <strong>"as is"</strong> and <strong>"as available"</strong>, without warranties of
            any kind, whether express or implied, including but not limited to warranties of merchantability, fitness
            for a particular purpose, non-infringement, accuracy, or that the Service will be uninterrupted, secure, or
            error-free.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">7. Limitation of Liability</h2>
          <p className="text-gray-600 leading-relaxed">
            To the maximum extent permitted by applicable law, VarBiz Ltd shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or
            goodwill, arising out of or in connection with your use of, or inability to use, the Service. Because the
            Service is provided free of charge, our total aggregate liability to you for any claim arising out of or
            relating to these Terms or the Service shall not exceed £50 (fifty pounds sterling).
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">8. Your Responsibility and Indemnity</h2>
          <p className="text-gray-600 leading-relaxed">
            You agree to indemnify and hold harmless VarBiz Ltd, its owners, directors, and staff from any claim, loss,
            liability, or expense (including reasonable legal fees) arising from your use of the Service, your breach
            of these Terms, or your reliance on any reminder or notification sent (or not sent) by the Service. Any
            impact on you resulting from a missed or delayed reminder is your responsibility to bear, and no liability
            for such impact rests with RemindMeOf or its owners.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">9. Third-Party Services and Advertising</h2>
          <p className="text-gray-600 leading-relaxed">
            The Service relies on third-party infrastructure, including Cloudflare (hosting), Resend (email delivery),
            and Google (sign-in and, where applicable, AdSense advertising). We are not responsible for outages,
            errors, or delays caused by these third parties. See our{' '}
            <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a> for details on advertising
            and cookies.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">10. Intellectual Property</h2>
          <p className="text-gray-600 leading-relaxed">
            The Service, including its design, branding, and underlying software, is owned by VarBiz Ltd and protected
            by applicable intellectual property laws. You retain ownership of the reminder content you create, but
            grant us a limited licence to store and process it solely to provide the Service to you.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">11. Suspension and Termination</h2>
          <p className="text-gray-600 leading-relaxed">
            We may suspend or terminate your account at any time if we reasonably believe you have breached these
            Terms or misused the Service. You may stop using the Service and delete your account at any time via your
            profile settings or by contacting us.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">12. Changes to These Terms</h2>
          <p className="text-gray-600 leading-relaxed">
            We may update these Terms from time to time. We will post the revised Terms on this page with an updated
            "Last updated" date. Continued use of the Service after changes take effect constitutes acceptance of the
            revised Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">13. Governing Law</h2>
          <p className="text-gray-600 leading-relaxed">
            These Terms are governed by the laws of England and Wales, and any disputes shall be subject to the
            exclusive jurisdiction of the courts of England and Wales.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">14. Contact</h2>
          <p className="text-gray-600 leading-relaxed">
            For any questions about these Terms, please contact VarBiz Ltd at{' '}
            <a href="mailto:contactus@varbiz.co.uk" className="text-blue-600 hover:underline">contactus@varbiz.co.uk</a>.
          </p>
        </section>

      </div>
    </div>
  );
}
